import React, { useEffect, useState, useMemo } from 'react'

// hooks
import { useTheme } from '@material-ui/core'

// types
import { Folder, traverse, Unit, UnitVisibility } from '../../models'

// components
import { Container } from '../Container'
import { Location, LocationSelection } from '../../store'
import { NEW_FOLDER_ID, ROOT_ID } from '../../hooks/useInfrastructure/types'
import { SortableFinderItem } from './SortableFinderItem'
import { SortableFinderFolder } from './SortableFinderFolder'
import { SortableFinderUnit } from './SortableFinderUnit'

interface Props {
    root?: Folder
    locationSelection: LocationSelection
    selectedIcon?: JSX.Element
    defaultOpen?: boolean
    /** Expand only the root folder so its immediate children are visible (collapsed). */
    defaultOpenFirstLevel?: boolean
    editFolderId?: number
    useCase?: 'inspection' | 'schedule'
    onClickFolder?: (folder: Location) => void
    onClickUnit?: (unit: Location) => void
    getUnitAdornment?: (unit: Unit) => JSX.Element | null
    getFolderAdornment?: (folder: Folder) => JSX.Element | null
    createFolder?: (parentId: number, name: string) => void
    editFolder?: (folderId: number, name: string) => void
    cancelCreateFolder?: () => void
    cancelEditFolder?: () => void
    enableSorting?: boolean
    defaultSortOrder?: 'asc' | 'desc'
    /** Sort folder and unit names so 2 comes before 10. */
    numericNameSort?: boolean
    /** Render every unit, including ones hidden from the schedule picker. */
    showAllUnits?: boolean
    showSelectionIcon?: boolean
    onSortChange?: (folderId: number, order: 'asc' | 'desc') => void
    onUnitOrderChange?: (folderId: number, unitIds: number[]) => void
    maxHeight?: string | number
}

const compareNames = (
    left: string,
    right: string,
    order: 'asc' | 'desc',
    numeric: boolean,
) => {
    const result = numeric
        ? left.localeCompare(right, undefined, {
              numeric: true,
              sensitivity: 'base',
          })
        : left.localeCompare(right)
    return order === 'asc' ? result : -result
}

interface FolderOpenState {
    [folderId: number]: boolean
}

interface FolderSortState {
    [folderId: number]: 'asc' | 'desc'
}

interface UnitOrderState {
    [folderId: number]: number[] // Array of unit IDs in order
}

const SortableFinderC: React.FC<Props> = (props) => {
    const {
        root,
        locationSelection,
        onClickFolder,
        onClickUnit,
        enableSorting = false,
        defaultSortOrder = 'asc',
        numericNameSort = false,
        showSelectionIcon = true,
    } = props

    const [openState, setOpenState] = useState<FolderOpenState>({})
    const [sortState, setSortState] = useState<FolderSortState>({})
    const [unitOrderState, setUnitOrderState] = useState<UnitOrderState>({})

    const theme = useTheme()

    useEffect(() => {
        if (root) {
            const newOpenState: FolderOpenState = { [-1]: true, [-2]: true }

            traverse(root, (folder) => {
                let isOpen = false
                if (folder.id in openState) {
                    isOpen = openState[folder.id]
                }
                newOpenState[folder.id] = isOpen
            })

            setOpenState(newOpenState)
        }
    }, [root])

    useEffect(() => {
        if (props.defaultOpen && root) {
            const newOpenState: FolderOpenState = { [-1]: true, [-2]: true }

            traverse(root, (folder) => {
                newOpenState[folder.id] = true
            })

            setOpenState(newOpenState)
        }
    }, [props.defaultOpen, root])

    useEffect(() => {
        if (props.defaultOpen || !props.defaultOpenFirstLevel || !root) {
            return
        }
        const newOpenState: FolderOpenState = { [-1]: true, [-2]: true }
        traverse(root, (folder) => {
            newOpenState[folder.id] = false
        })
        newOpenState[root.id] = true
        setOpenState(newOpenState)
    }, [props.defaultOpen, props.defaultOpenFirstLevel, root])

    // Update unit order when sorting changes (only when sortState actually changes)
    useEffect(() => {
        if (!root || !enableSorting || Object.keys(sortState).length === 0)
            return

        const updateUnitOrder = (folder: Folder) => {
            // Only update if this folder has a sort order set
            if (sortState[folder.id]) {
                const sortedUnits = sortItems(folder.units, folder.id)
                const unitIds = sortedUnits.map((unit) => unit.id)

                if (unitIds.length > 0) {
                    setUnitOrderState((prev) => ({
                        ...prev,
                        [folder.id]: unitIds,
                    }))

                    if (props.onUnitOrderChange) {
                        props.onUnitOrderChange(folder.id, unitIds)
                    }
                }
            }

            folder.children.forEach((child) => updateUnitOrder(child))
        }

        updateUnitOrder(root)
    }, [sortState, root, enableSorting])

    const handleSortToggle = (folderId: number) => {
        const currentSort = sortState[folderId] || defaultSortOrder
        const newSort = currentSort === 'asc' ? 'desc' : 'asc'

        setSortState((prev) => ({
            ...prev,
            [folderId]: newSort as 'asc' | 'desc',
        }))

        // Immediately update unit order for this folder
        const updateFolderUnitOrder = (folder: Folder) => {
            if (folder.id === folderId) {
                // Sort with the new order directly
                const sortedUnits = [...folder.units].sort((a, b) =>
                    compareNames(a.name, b.name, newSort, numericNameSort),
                )
                const unitIds = sortedUnits.map((unit) => unit.id)

                if (unitIds.length > 0) {
                    setUnitOrderState((prev) => ({
                        ...prev,
                        [folder.id]: unitIds,
                    }))

                    if (props.onUnitOrderChange) {
                        props.onUnitOrderChange(folder.id, unitIds)
                    }
                }
            }

            folder.children.forEach((child) => updateFolderUnitOrder(child))
        }

        if (root) {
            updateFolderUnitOrder(root)
        }

        if (props.onSortChange) {
            props.onSortChange(folderId, newSort)
        }
    }

    const sortItems = <T extends { name: string; id: number }>(
        items: T[],
        folderId: number,
    ): T[] => {
        if (!enableSorting) return items

        const sortOrder = sortState[folderId] || defaultSortOrder
        const sortedItems = [...items].sort((a, b) =>
            compareNames(a.name, b.name, sortOrder, numericNameSort),
        )

        return sortedItems
    }

    const renderedTree: JSX.Element[] = []

    const renderFolder = (folder: Folder, level = 0) => {
        const selected = locationSelection.folder[folder.id] !== undefined
        const hasChildren =
            folder.children.length > 0 || folder.units.length > 0

        if (folder.id === NEW_FOLDER_ID) {
            // Handle new folder creation (if needed)
            return
        } else if (folder.id === props.editFolderId) {
            // Handle folder editing (if needed)
            return
        } else {
            const sortedChildren = sortItems(folder.children, folder.id)
            const sortedUnits = sortItems(folder.units, folder.id)

            renderedTree.push(
                <SortableFinderItem
                    level={level}
                    selectedIcon={props.selectedIcon}
                    key={`SORTABLE_FINDER_FOLDER_${folder.id}`}
                    myKey={`SORTABLE_FINDER_FOLDER_${folder.id}`}
                    onClickIcon={() => {
                        if (onClickFolder) {
                            onClickFolder({
                                type: 'folder',
                                location: folder,
                            })
                        }
                    }}
                    showSelectionIcon={showSelectionIcon}
                    selected={selected}
                >
                    <SortableFinderFolder
                        folder={folder}
                        open={openState[folder.id]}
                        onClick={() => {
                            setOpenState({
                                ...openState,
                                [folder.id]: !openState[folder.id],
                            })
                        }}
                        getFolderAdornment={props.getFolderAdornment}
                        enableSorting={enableSorting}
                        sortOrder={sortState[folder.id] || defaultSortOrder}
                        onSortToggle={() => handleSortToggle(folder.id)}
                        hasChildren={hasChildren}
                    />
                </SortableFinderItem>,
            )

            if (openState[folder.id]) {
                // Render sorted child folders
                sortedChildren.forEach((child) => {
                    renderFolder(child, level + 1)
                })

                // Render sorted units
                sortedUnits.forEach((unit) => {
                    // Determine if unit should be visible based on useCase
                    const isVisible =
                        props.showAllUnits ||
                        (() => {
                            switch (props.useCase) {
                                case 'inspection':
                                    return (
                                        unit.visibility ===
                                            UnitVisibility.INSPECTION_ONLY ||
                                        unit.visibility ===
                                            UnitVisibility.ALWAYS_VISIBLE
                                    )
                                case 'schedule':
                                    return (
                                        unit.visibility ===
                                            UnitVisibility.SCHEDULE_ONLY ||
                                        unit.visibility ===
                                            UnitVisibility.ALWAYS_VISIBLE
                                    )
                                default:
                                    return (
                                        unit.visibility !==
                                        UnitVisibility.NOT_VISIBLE
                                    )
                            }
                        })()

                    // Skip rendering if unit is not visible in current context
                    if (!isVisible) return

                    const selected =
                        locationSelection.unit[unit.id] !== undefined

                    renderedTree.push(
                        <SortableFinderItem
                            level={level + 1}
                            selectedIcon={props.selectedIcon}
                            key={`SORTABLE_FINDER_UNIT_${unit.id}`}
                            myKey={`SORTABLE_FINDER_UNIT_${unit.id}`}
                            onClickIcon={() => {
                                onClickUnit &&
                                    onClickUnit({
                                        type: 'unit',
                                        location: unit,
                                    })
                            }}
                            showSelectionIcon={showSelectionIcon}
                            selected={selected}
                        >
                            <SortableFinderUnit
                                unit={unit}
                                getUnitAdornment={props.getUnitAdornment}
                            />
                        </SortableFinderItem>,
                    )
                })
            }
        }
    }

    if (root) {
        renderFolder(root, 0)
    }

    return (
        <Container
            direction="column"
            flex={1}
            style={{
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.shape.borderRadius,
                padding: theme.spacing(1),
                height: '100%',
                maxHeight: props.maxHeight || '100%',
            }}
            scrollY
        >
            {renderedTree}
        </Container>
    )
}

export const SortableFinder: React.FC<Props> = React.memo(SortableFinderC)
